import { useEffect, useState } from 'react';
import heroImage from '@/assets/for-leaders-hero.jpg';

const LeadersHero = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="relative min-h-[70vh] sm:min-h-[85vh] flex items-end overflow-hidden">
      {/* Painterly background with slow zoom + parallax drift */}
      <div
        className="absolute inset-0 bg-cover bg-left sm:bg-center bg-no-repeat will-change-transform animate-[heroZoom_24s_ease-out_forwards]"
        style={{
          backgroundImage: `url(${heroImage})`,
          transform: `translate3d(0, ${scrollY * 0.12}px, 0)`,
        }}
        aria-hidden="true"
      />

      {/* Layered overlays — vignette + bottom anchor for legibility */}
      <div className="absolute inset-0 bg-foreground/75" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/60 to-foreground/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground/70 via-transparent to-transparent" />

      {/* Editorial composition — anchored lower-left */}
      <div className="container-mobile relative z-10 pb-10 sm:pb-28 pt-28 sm:pt-40 max-w-7xl mx-auto w-full">
        <div className="max-w-3xl">
          {/* Eyebrow with thin terracotta rule */}
          <div className="flex items-center gap-4 mb-8 animate-fade-in">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              For Leaders
            </p>
          </div>

          <h1
            className="text-background text-4xl sm:text-6xl lg:text-7xl xl:text-8xl mb-10 leading-[1] sm:leading-[0.95] animate-fade-in"
            style={{ textWrap: 'balance', animationDelay: '120ms', animationFillMode: 'backwards' }}
          >
            The work no one else{' '}
            <span className="text-primary italic font-display">can do for you.</span>
          </h1>

          <p
            className="text-background/85 text-xl sm:text-2xl leading-relaxed max-w-2xl font-light animate-fade-in"
            style={{ animationDelay: '240ms', animationFillMode: 'backwards' }}
          >
            A private, sustained coaching practice for the leader who senses
            that the next level of impact requires a different level of being.
          </p>
        </div>
      </div>

      {/* Bottom horizon rule — visual continuity to the rest of the page */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-background/30 to-transparent z-10" />

      <style>{`
        @keyframes heroZoom {
          from { transform: scale(1.08); }
          to { transform: scale(1); }
        }
      `}</style>
    </section>
  );
};

export default LeadersHero;
